import React, { useState, useMemo, useContext, createContext, useEffect } from 'react';
import { TableHeaderRow } from '@devexpress/dx-react-grid-material-ui';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  Popover,
  TextField
} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SearchIcon from '@mui/icons-material/Search';
import PropTypes from 'prop-types';

// Shared context so HeaderFilterCell components can read rows + filters without
// prop-drilling through the DX grid internals.
export const HeaderFilterContext = createContext({
  rows: [],
  columnFilters: {},
  onFilterChange: () => {},
  loadFilterOptions: null
});

export const getRowCellValue = (row, columnName) => {
  const val = row[columnName];
  if (val === undefined || val === null || val === '') return '(Blank)';
  if (typeof val === 'object') {
    const displayValue = val.name || val.label || val.DisplayName || val.FullyQualifiedName;
    if (displayValue !== undefined && displayValue !== null && displayValue !== '') return String(displayValue);
    if (val.amount !== undefined && val.amount !== null) return String(val.amount);
    const fileValue = val.name || val.webUrl || val.path;
    if (fileValue) return String(fileValue);
    return '(Blank)';
  }
  return String(val);
};

const HeaderFilterCell = ({ column, children, ...restProps }) => {
  const { rows, columnFilters, onFilterChange, loadFilterOptions } = useContext(HeaderFilterContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [remoteOptions, setRemoteOptions] = useState({ values: [], total: 0 });
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsError, setOptionsError] = useState('');
  const usesRemoteOptions = typeof loadFilterOptions === 'function';

  const uniqueValues = useMemo(() => {
    if (!rows || rows.length === 0) return [];
    const values = rows.map((row) => getRowCellValue(row, column.name));
    // eslint-disable-next-line no-undef
    return Array.from(new Set(values)).sort((a, b) => {
      if (a === '(Blank)') return 1;
      if (b === '(Blank)') return -1;
      return a.localeCompare(b);
    });
  }, [rows, column.name]);

  const activeFilters = columnFilters[column.name] || [];
  const isFiltered = activeFilters.length > 0;

  const [tempSelected, setTempSelected] = useState([]);

  // DX can reuse a header-cell instance when visibility or search results
  // change. Never carry an open popover or temporary selection to a new column.
  useEffect(() => {
    setAnchorEl(null);
    setSearchTerm('');
    setTempSelected([]);
    setRemoteOptions({ values: [], total: 0 });
    setOptionsError('');
  }, [column.name]);

  useEffect(() => {
    if (!anchorEl || !usesRemoteOptions) return undefined;

    let active = true;
    setOptionsLoading(true);
    setOptionsError('');
    // eslint-disable-next-line no-undef
    const timeout = window.setTimeout(
      () => {
        loadFilterOptions(column.name, searchTerm.trim())
          .then((result) => {
            if (!active) return;
            const values = Array.isArray(result?.values) ? result.values.map(String) : [];
            setRemoteOptions({
              values,
              total: Number.isFinite(Number(result?.total)) ? Number(result.total) : values.length
            });
          })
          .catch(() => {
            if (!active) return;
            setRemoteOptions({ values: [], total: 0 });
            setOptionsError('Could not load filter values');
          })
          .finally(() => {
            if (active) setOptionsLoading(false);
          });
      },
      searchTerm ? 300 : 0
    );

    return () => {
      active = false;
      // eslint-disable-next-line no-undef
      window.clearTimeout(timeout);
    };
  }, [anchorEl, column.name, loadFilterOptions, searchTerm, usesRemoteOptions]);

  const filteredValues = useMemo(() => {
    if (usesRemoteOptions) return remoteOptions.values;
    if (!searchTerm.trim()) return uniqueValues;
    const term = searchTerm.toLowerCase();
    return uniqueValues.filter((val) => val.toLowerCase().includes(term));
  }, [remoteOptions.values, searchTerm, uniqueValues, usesRemoteOptions]);

  const handleOpen = (event) => {
    event.stopPropagation();
    const currentActive = columnFilters[column.name];
    setTempSelected(currentActive && currentActive.length > 0 ? currentActive : []);
    setSearchTerm('');
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleToggleValue = (value) => {
    setTempSelected((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  };

  const handleToggleSelectAll = () => {
    const allVisibleSelected = filteredValues.every((v) => tempSelected.includes(v));
    if (allVisibleSelected) {
      setTempSelected((prev) => prev.filter((v) => !filteredValues.includes(v)));
    } else {
      // eslint-disable-next-line no-undef
      setTempSelected((prev) => Array.from(new Set([...prev, ...filteredValues])));
    }
  };

  const handleApply = () => {
    const selectedAllRemoteValues =
      usesRemoteOptions &&
      !searchTerm &&
      remoteOptions.total === filteredValues.length &&
      filteredValues.every((value) => tempSelected.includes(value));
    if (
      (!usesRemoteOptions && tempSelected.length === uniqueValues.length) ||
      selectedAllRemoteValues ||
      tempSelected.length === 0
    ) {
      const newFilters = { ...columnFilters };
      delete newFilters[column.name];
      onFilterChange(newFilters);
    } else {
      onFilterChange({ ...columnFilters, [column.name]: tempSelected });
    }
    handleClose();
  };

  const handleClear = () => {
    const newFilters = { ...columnFilters };
    delete newFilters[column.name];
    onFilterChange(newFilters);
    handleClose();
  };

  const showFilterIcon = usesRemoteOptions || uniqueValues.length > 1 || isFiltered;
  const optionTotal = usesRemoteOptions ? remoteOptions.total : filteredValues.length;
  const hasMoreRemoteOptions = usesRemoteOptions && optionTotal > filteredValues.length;
  const selectAllLabel = hasMoreRemoteOptions
    ? `Select all ${filteredValues.length} shown (${optionTotal} ${searchTerm ? 'matching' : 'total'})`
    : searchTerm
    ? `Select all ${filteredValues.length} matching`
    : `Select all ${filteredValues.length} values`;

  return (
    <TableHeaderRow.Cell column={column} {...restProps}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{children}</span>
        {showFilterIcon && (
          <IconButton
            size="small"
            aria-label={`Filter ${column.title || column.name}`}
            onClick={handleOpen}
            onMouseDown={(e) => e.stopPropagation()}
            sx={{
              padding: '2px',
              color: isFiltered ? 'primary.main' : 'action.active',
              '&:hover': { backgroundColor: 'action.hover' }
            }}
          >
            <FilterAltIcon sx={{ fontSize: '1.1rem' }} />
          </IconButton>
        )}
      </Box>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: { padding: 1, width: 250, maxHeight: 400, display: 'flex', flexDirection: 'column' }
        }}
      >
        <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <TextField
            size="small"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
            autoFocus
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={filteredValues.length > 0 && filteredValues.every((v) => tempSelected.includes(v))}
                indeterminate={
                  filteredValues.some((v) => tempSelected.includes(v)) &&
                  !filteredValues.every((v) => tempSelected.includes(v))
                }
                onChange={handleToggleSelectAll}
                disabled={optionsLoading || filteredValues.length === 0}
                size="small"
              />
            }
            label={selectAllLabel}
          />
          <Box sx={{ px: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>{tempSelected.length} selected</Box>
          <Divider />
          <Box sx={{ maxHeight: 160, overflowY: 'auto', display: 'flex', flexDirection: 'column', pl: 1 }}>
            <FormGroup>
              {optionsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={22} />
                </Box>
              ) : optionsError ? (
                <Box sx={{ p: 1, color: 'error.main', fontSize: '0.875rem' }}>{optionsError}</Box>
              ) : filteredValues.length === 0 ? (
                <Box sx={{ p: 1, color: 'text.secondary', fontSize: '0.875rem' }}>No matches</Box>
              ) : (
                filteredValues.map((val) => (
                  <FormControlLabel
                    key={val}
                    control={
                      <Checkbox
                        checked={tempSelected.includes(val)}
                        onChange={() => handleToggleValue(val)}
                        size="small"
                      />
                    }
                    label={val}
                  />
                ))
              )}
            </FormGroup>
          </Box>
          <Divider />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
            <Button size="small" onClick={handleClear} disabled={!isFiltered}>
              Clear
            </Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" variant="outlined" onClick={handleClose}>
                Cancel
              </Button>
              <Button size="small" variant="contained" onClick={handleApply}>
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Popover>
    </TableHeaderRow.Cell>
  );
};

HeaderFilterCell.propTypes = {
  column: PropTypes.any,
  children: PropTypes.node
};

export default HeaderFilterCell;
